import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { components } from '../generated/api';
import { setUserContext, trackUserJourney } from '../utils/datadog';
import { getRumUser } from '../utils/users';
import './LoginPage.css';

type BotDetectionRequest = components['schemas']['BotDetectionRequest'];
type BotDetectionResponse = components['schemas']['BotDetectionResponse'];
type LoginResponse = components['schemas']['LoginResponse'];
type LoginScenarioResponse = components['schemas']['LoginScenarioResponse'];
type Login = components['schemas']['Login'];

const LoginPage: React.FC = () => {
  const navigate = useNavigate();

  // Get or assign RUM user for Product Analytics
  const rumUser = getRumUser();

  const [username, setUsername] = useState(rumUser);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDemoScenarios, setShowDemoScenarios] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [scenarios, setScenarios] = useState<LoginScenarioResponse | null>(null);

  React.useEffect(() => {
    document.title = 'Bitsburg Bank - Login';

    // Track user journey step
    trackUserJourney('login_page_visited', {
      previous_page: document.referrer || 'direct',
      timestamp: new Date().toISOString(),
    });

    // Check for TypeError occurrence
    if (Math.random() < 0.1) {
      try {
        window.DD_LOGS?.logger?.info('Checking for undefined property access on login page');
        const undefinedObj: any = undefined;
        undefinedObj.someProperty.value;
      } catch (error) {
        window.DD_LOGS?.logger?.error('Undefined property access error on login page', { error: error });
        window.DD_RUM?.addError(error);
      }
    }

    // Fetch scenarios from API and pre-populate with successful login credentials
    fetch('/api/login/scenarios')
      .then((response) => response.json())
      .then((data) => {
        setScenarios(data as LoginScenarioResponse);
        // Pre-populate password with successful login password
        const successPassword = data.scenarios?.SuccessfulLogin?.password;
        if (successPassword) {
          setPassword(successPassword);
        }
      })
      .catch((error) => window.DD_LOGS?.logger?.error('Error fetching scenarios', { error: error }));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setShowErrorAlert(false);

    // Check if a frontend exception should be thrown
    if (username === 'username' && password === 'frontend') {
      setErrorMessage('Frontend Exception during login.');
      setShowErrorAlert(true);
      setIsLoading(false);
      return;
    }

    try {
      // Bot detection check via Security API
      const botDetectionRequest: BotDetectionRequest = {
        userAgent: navigator.userAgent,
      };

      const botResponse = await fetch('/api/security/bot/detection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(botDetectionRequest),
      });

      if (!botResponse.ok) {
        setErrorMessage('Security check failed. Please try again later.');
        setShowErrorAlert(true);
        return;
      }

      const botResult = (await botResponse.json()) as BotDetectionResponse;

      if (botResult.flagged === true) {
        setErrorMessage('Security verification failed. Please contact support if you believe this is an error.');
        setShowErrorAlert(true);
        return;
      }

      // Proceed with login attempt
      const loginResponse = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username,
          password: password,
        } as Login),
      });

      if (loginResponse.ok) {
        // Parse the successful login response
        const loginResult = (await loginResponse.json()) as LoginResponse;
        window.DD_LOGS?.logger?.info('Login successful', { message: loginResult.message });

        // Create consistent timestamp for user context and journey tracking
        const currentTimestamp = new Date().toISOString();

        // Set user context once for the entire RUM session (using login username)
        setUserContext(username, {
          login_method: 'password',
          login_timestamp: currentTimestamp,
        });

        // Track successful login journey step
        trackUserJourney('login_successful', {
          next_page: 'account',
          timestamp: currentTimestamp,
        });

        navigate('/account');
        return;
      } else if (loginResponse.status >= 400 && loginResponse.status < 500) {
        if (loginResponse.status === 403) {
          setUserContext(username, {
            login_method: 'password',
            login_timestamp: new Date().toISOString(),
            login_result: 'account_locked',
          });
          setErrorMessage('Account locked. Please contact support.');
        } else {
          setErrorMessage('Invalid credentials. Please try again.');
        }
        setShowErrorAlert(true);
      } else if (loginResponse.status >= 500) {
        setUserContext(username, {
          login_method: 'password',
          login_timestamp: new Date().toISOString(),
          login_result: loginResponse.status === 503 ? 'server_maintenance' : 'server_error',
        });

        if (loginResponse.status === 503) {
          setErrorMessage('Server maintenance. Please try again later.');
        } else {
          setErrorMessage('Server error. Please try again later.');
        }
        setShowErrorAlert(true);
      } else {
        setErrorMessage('Invalid credentials. Please try again.');
        setShowErrorAlert(true);
      }
    } catch (error) {
      window.DD_LOGS?.logger?.error('Error during login', { error: error });
      setErrorMessage('An unexpected error occurred. Please try again later.');
      setShowErrorAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const populateFields = (scenario: string) => {
    const creds = scenarios?.scenarios?.[scenario];
    if (creds) {
      // Use the random RUM user for all scenarios
      setUsername(rumUser);
      setPassword(creds.password ?? '');
    }

    if (scenario === 'FrontendException') {
      throw new Error('Frontend Exception during login.');
    }
  };

  return (
    <>
      {/* Bank Login Section */}
      <div className="py-5">
        <div className="container">
          <div className="row justify-content-center align-items-center min-vh-50">
            <div className="col-lg-5 col-md-7 col-sm-9">
              <div className="bank-card bank-card-elevated bank-card-no-hover">
                <div className="bank-card-header">
                  <h3 className="mb-0 fw-bold">
                    <i className="bi bi-shield-lock me-2"></i>
                    Online Banking Login
                  </h3>
                  <p className="mb-0 mt-2 opacity-75">Secure access to your Bitsburg Bank account</p>
                </div>
                <div className="bank-card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                      <label htmlFor="username" className="bank-form-label">
                        Username
                      </label>
                      <input
                        type="text"
                        className="form-control bank-form-control"
                        id="username"
                        maxLength={20}
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="Enter your username"
                        autoComplete="username"
                      />
                    </div>
                    <div className="mb-4">
                      <label htmlFor="password" className="bank-form-label">
                        Password
                      </label>
                      <div className="input-group bank-input-group">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          className="form-control bank-form-control"
                          id="password"
                          maxLength={25}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          autoComplete="current-password"
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                        </button>
                      </div>
                    </div>
                    <div className="text-center">
                      <button
                        type="submit"
                        className="bank-btn-primary px-4"
                        disabled={isLoading}
                        id="login-submit-btn"
                      >
                        {!isLoading ? (
                          <span>
                            <i className="bi bi-box-arrow-in-right me-2"></i>
                            <span>Sign In to Your Account</span>
                          </span>
                        ) : (
                          <span>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            <span>Signing In...</span>
                          </span>
                        )}
                      </button>
                    </div>

                    <div className="text-center mt-3">
                      <Link
                        to="/forgotpassword"
                        className="text-datadog-purple text-decoration-none me-3"
                        id="login-forgot-password-link"
                      >
                        <i className="bi bi-key me-1"></i>
                        Forgot Password?
                      </Link>
                      <span className="text-muted">|</span>
                      <Link
                        to="/enroll"
                        className="text-datadog-purple text-decoration-none ms-3"
                        id="login-enroll-link"
                      >
                        <i className="bi bi-person-plus me-1"></i>
                        Enroll Now
                      </Link>
                    </div>
                  </form>

                  {/* Error Alert Section */}
                  {showErrorAlert && (
                    <div className="mt-4">
                      <div className="alert bank-alert bank-alert-danger alert-dismissible fade show" role="alert">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {errorMessage}
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowErrorAlert(false)}
                          aria-label="Close"
                        ></button>
                      </div>
                    </div>
                  )}

                  <div className="text-center mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setShowDemoScenarios(!showDemoScenarios)}
                      id="login-show-demo-scenarios-btn"
                    >
                      <i className="bi bi-gear me-2"></i>
                      <span>{showDemoScenarios ? 'Hide' : 'Show'} Demo Scenarios</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Scenarios Section */}
      <section
        className={`pt-1 pb-3 ${showDemoScenarios ? 'show' : ''}`}
        style={{
          transition: 'all 0.4s ease-in-out',
          overflow: 'hidden',
          maxHeight: showDemoScenarios ? '800px' : '0',
          opacity: showDemoScenarios ? '1' : '0',
        }}
      >
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 col-md-10">
              <div className="bank-card bank-card-no-hover">
                <div className="card-header bank-gradient-light text-center py-3">
                  <h4 className="mb-0 fw-bold text-datadog-purple">
                    <i className="bi bi-play-circle me-2"></i>
                    Demo Scenarios
                  </h4>
                  <p className="mb-0 mt-2 text-muted">Click any scenario to test different login outcomes</p>
                </div>
                <div className="bank-card-body">
                  <div className="row g-3">
                    <div className="col-sm-6 col-lg-4">
                      <button
                        type="button"
                        className="btn btn-success w-100 scenario-btn"
                        onClick={() => populateFields('SuccessfulLogin')}
                        id="login-scenario-successful-btn"
                      >
                        <i className="bi bi-check-circle me-2"></i>
                        Successful Login
                      </button>
                    </div>
                    <div className="col-sm-6 col-lg-4">
                      <button
                        type="button"
                        className="btn btn-warning w-100 scenario-btn"
                        onClick={() => populateFields('InvalidCredentials')}
                        id="login-scenario-invalid-btn"
                      >
                        <i className="bi bi-x-circle me-2"></i>
                        Invalid Credentials
                      </button>
                    </div>
                    <div className="col-sm-6 col-lg-4">
                      <button
                        type="button"
                        className="btn btn-danger w-100 scenario-btn"
                        onClick={() => populateFields('AccountLocked')}
                        id="login-scenario-locked-btn"
                      >
                        <i className="bi bi-lock me-2"></i>
                        Account Locked
                      </button>
                    </div>
                    <div className="col-sm-6 col-lg-4">
                      <button
                        type="button"
                        className="btn btn-danger w-100 scenario-btn"
                        onClick={() => populateFields('BackendError')}
                        id="login-scenario-backend-error-btn"
                      >
                        <i className="bi bi-server me-2"></i>
                        Backend Error
                      </button>
                    </div>
                    <div className="col-sm-6 col-lg-4">
                      <button
                        type="button"
                        className="btn btn-danger w-100 scenario-btn"
                        onClick={() => populateFields('BackendException')}
                        id="login-scenario-backend-exception-btn"
                      >
                        <i className="bi bi-bug me-2"></i>
                        Backend Exception
                      </button>
                    </div>
                    <div className="col-sm-6 col-lg-4">
                      <button
                        type="button"
                        className="btn btn-danger w-100 scenario-btn"
                        onClick={() => populateFields('BackendMaintenance')}
                        id="login-scenario-backend-maintenance-btn"
                      >
                        <i className="bi bi-tools me-2"></i>
                        Backend Maintenance
                      </button>
                    </div>
                    <div className="col-sm-6 col-lg-4">
                      <button
                        type="button"
                        className="btn btn-danger w-100 scenario-btn"
                        onClick={() => populateFields('BackendTimeout')}
                        id="login-scenario-backend-timeout-btn"
                      >
                        <i className="bi bi-clock me-2"></i>
                        Backend Timeout
                      </button>
                    </div>
                    <div className="col-sm-6 col-lg-4">
                      <button
                        type="button"
                        className="btn btn-danger w-100 scenario-btn"
                        onClick={() => populateFields('FrontendException')}
                        id="login-scenario-frontend-exception-btn"
                      >
                        <i className="bi bi-exclamation-diamond me-2"></i>
                        Frontend Exception
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default LoginPage;
